/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useEffect, useState } from 'react';

// Mantine
import {
	FileInputProps,
	MantineSize,
	FileInput as NativeFileInput,
	Pill,
	Tooltip,
} from '@mantine/core';

// Styles
import classes from './FileInput.module.css';
import './customStyle.css';

// I18n
import { useTranslation } from 'react-i18next';

// Utils
import {
	isNumber,
	truncateString,
} from '@utils/functions';

// Mantine notifications
import { notifications } from '@mantine/notifications';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */
type TFileInputProps = {
	accept?: string;
	maxsize?: number;
	size?: MantineSize | (string & {});
	clearable?: boolean;
	withAsterisk?: boolean;
	error?: React.ReactNode;
	label?: React.ReactNode;
	onChange?: (payload: File | null) => void;
	value?: File | null;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const FileInput = (props: TFileInputProps) => {
	const {
		clearable,
		onChange,
		maxsize,
		error,
		label,
		size,
		value: nativeValue,
		withAsterisk,
		accept,
	} = props;

	const { t } = useTranslation();

	const [value, setValue] = useState<File | null>(null);

	useEffect(() => {
		if (nativeValue) {
			setValue(nativeValue);
		}
	}, [nativeValue]);

	const handleOnChange = (file: File | null) => {
		if (file && isNumber(maxsize)) {
			const sizeMb = Number(
				(file.size / (1024 * 1024)).toFixed(2)
			);
			if (maxsize && Number(sizeMb) > maxsize) {
				notifications.show({
					title: t('global.error'),
					message: `${t('errors.fileTooLarge')} ${maxsize} ${t('global.megabyte')}`,
					color: 'var(--mantine-color-igluRed-9)',
				});
				return;
			}
		}

		if (file && onChange) {
			setValue(file);
			onChange(file);
		} else if (onChange) {
			onChange(null);
			setValue(null);
		}
	};

	const ValueComponent: FileInputProps['valueComponent'] =
		({ value }) => {
			if (value === null) {
				return null;
			}

			const sizeInMB: File | File[] = value;
			if (Array.isArray(sizeInMB)) {
				return null;
			}

			const size = (
				sizeInMB.size /
				(1024 * 1024)
			).toFixed(2);

			if (props.maxsize && Number(size) > props.maxsize) {
				return null;
			}

			if (Array.isArray(value)) {
				return (
					<Pill.Group>
						{value.map((file) => (
							<Pill>{truncateString(file.name, 15)}</Pill>
						))}
					</Pill.Group>
				);
			}

			return (
				<Tooltip
					label={value.name}
					events={{
						hover: value.name.length > 20,
						focus: value.name.length > 20,
						touch: false,
					}}
				>
					<Pill>{truncateString(value.name, 20)}</Pill>
				</Tooltip>
			);
		};

	return (
		<NativeFileInput
			accept={accept}
			withAsterisk={withAsterisk}
			data-testid="D2DtDXR7_Z5jdHi6B-fileinput"
			size={size}
			label={label}
			error={error}
			clearable={clearable}
			placeholder={t('global.browse')}
			classNames={classes}
			valueComponent={ValueComponent}
			value={value}
			onChange={handleOnChange}
		/>
	);
};
